/**
 * Base-path aware URL helpers.
 *
 * The site is served from the apex domain (a live CNAME is committed in
 * public/, and bndl-co.github.io/lux-web/ 301s to luxfinancial.io), so BASE is
 * normally "/". Building with BASE_PATH=/lux-web/ makes every asset and
 * internal link resolve under a subpath instead, which is what GitHub Pages
 * needs if the custom domain is ever removed.
 */

// `process` does not exist in the browser, so this must be a build-time
// substitution rather than a runtime lookup. scripts/build.tsx passes
// --define for BUILD_BASE_PATH; the `typeof` guard keeps the module usable
// from Bun (prerender, scripts) where process IS available.
declare const BUILD_BASE_PATH: string | undefined;

const raw =
  typeof BUILD_BASE_PATH === 'string'
    ? BUILD_BASE_PATH
    : typeof process !== 'undefined'
      ? (process.env.BASE_PATH ?? '/')
      : '/';

/** Always exactly one leading and one trailing slash, e.g. "/" or "/lux-web/". */
export const BASE = `/${raw.replace(/^\/+|\/+$/g, '')}/`.replace('//', '/');

/** Resolve a repo-relative asset path (e.g. "images/hero.webp"). */
export function asset(path: string): string {
  return BASE + path.replace(/^\/+/, '');
}

/**
 * Resolve an internal route href. Accepts "/", "/about", "/#contact",
 * "/policies#privacy-policy". External URLs and tel:/mailto: pass through
 * untouched.
 */
export function href(to: string): string {
  if (/^([a-z]+:)?\/\//i.test(to) || /^(tel|mailto):/i.test(to)) return to;
  const [pathPart = '', hashPart] = to.split('#');
  const clean = pathPart.replace(/^\/+/, '');
  const base = clean === '' ? BASE : BASE + clean;
  return hashPart ? `${base}#${hashPart}` : base;
}

/** Absolute URL for canonical / OG tags. */
export function absoluteUrl(path: string, origin: string): string {
  const p = path.replace(/^\/+/, '');
  return p === '' ? `${origin}/` : `${origin}/${p}`;
}
